import { useEffect, useState } from "react";
import { pillarsApi, type ExportsManifest } from "../../lib/pillars";
import { Card } from "./chartkit";

/**
 * What the model is built on, to look at and to take away.
 *
 * The year-by-year table comes first because it answers the question a download is
 * usually for — "is this really sixteen years of data?" — without downloading
 * anything. The files are then the means to check it properly: plain CSV, zipped,
 * each with a README describing every column.
 */
export function DataView({ onError }: { onError: (e: unknown) => void }) {
  const [data, setData] = useState<ExportsManifest | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    pillarsApi
      .exports()
      .then(setData)
      .catch((e) => {
        if (e instanceof Error && e.message.includes("503")) setMissing(true);
        else onError(e);
      });
  }, [onError]);

  if (missing) {
    return (
      <p className="p-3 text-[11px] text-term-dim">
        No extracts have been built yet. Run <span className="text-term-text">srank export-data</span> and
        sync.
      </p>
    );
  }
  if (!data) return null;

  return (
    <div className="p-3 space-y-3">
      <p className="text-[11px] leading-relaxed text-term-dim max-w-3xl">
        Built {data.built_at.slice(0, 10)}. Fundamentals run to filings dated{" "}
        <span className="tnum text-term-text">{data.fundamentals_through}</span>; the SEC publishes
        its archives a quarter in arrears, so the most recent fiscal year is incomplete. Raw daily
        prices are not included: they were licensed for this project's own use. Market caps, scores
        and monthly returns are figures computed from them.
      </p>

      <div className="grid gap-3 grid-cols-1 xl:grid-cols-2">
        {data.files.map((f) => (
          <Card
            key={f.name}
            title={f.title}
            subtitle={`${f.rows.toLocaleString()} rows · ${f.companies.toLocaleString()} companies · ${f.from} → ${f.to}`}
            height="auto"
            right={
              <a
                href={`/api/v1/exports/${f.name}`}
                download={f.name}
                className="shrink-0 px-3 py-1 rounded border border-term-accent text-term-accent text-[11px] hover:bg-term-accent/20"
              >
                Download · {(f.bytes / 1e6).toFixed(1)} MB
              </a>
            }
          >
            <p className="px-1 pb-2 text-[11px] leading-relaxed text-term-dim">{f.description}</p>
            <details className="px-1">
              <summary className="cursor-pointer text-[11px] text-term-text">
                {f.columns.length} columns
              </summary>
              <table className="mt-1 w-full text-[11px]">
                <tbody>
                  {f.columns.map((c) => (
                    <tr key={c.name} className="border-t border-term-edge/50 align-top">
                      <td className="py-1 pr-3 text-term-accent whitespace-nowrap">{c.name}</td>
                      <td className="py-1 text-term-dim">{c.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </Card>
        ))}
      </div>

      <Card
        title="What the data holds, year by year"
        subtitle="Month-ends ranked, distinct companies ranked in the year, the average number investable in a month, and companies with a fiscal year of reported fundamentals ending in that year."
        height="auto"
      >
        <table className="w-full text-[12px] tnum">
          <thead className="text-[10px] uppercase tracking-wide text-term-dim">
            <tr className="border-b border-term-edge">
              <th className="text-left px-2 py-1">Year</th>
              <th className="text-right px-2">Month-ends</th>
              <th className="text-right px-2">Companies ranked</th>
              <th className="text-right px-2">Average per month</th>
              <th className="text-right px-2">With fundamentals</th>
            </tr>
          </thead>
          <tbody>
            {data.by_year.map((y) => (
              <tr key={y.year} className="border-b border-term-edge/50">
                <td className="px-2 py-1">{y.year}</td>
                <td className="text-right px-2">{y.month_ends ?? "—"}</td>
                <td className="text-right px-2">{y.companies_ranked?.toLocaleString() ?? "—"}</td>
                <td className="text-right px-2">{y.avg_per_month?.toLocaleString() ?? "—"}</td>
                <td className="text-right px-2">
                  {y.companies_with_fundamentals?.toLocaleString() ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
